import React from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import DateFnsUtils from '@date-io/date-fns';
import Grid from '@material-ui/core/Grid';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import Typography from '@material-ui/core/Typography';
import moment from 'moment/moment';
import ContentCategoryViewer from '../ContentCategoryViewer/ContentCategoryViewer';
import useStyles from './styles';
import { DATE_PICKER_FORMAT, DATE_PICKER_VALUE_FORMAT, DATE_SHORT_FORMAT } from '../../constants/appConstants';

function UserProductInformation({
  product,
  isDateEditMode,
  isSaveDisabled,
  setExpireDate,
  setIsSaveDisabled,
  isComingFromSeatsDetailsPage,
}) {
  const classes = useStyles();

  const isValidDate = (date) => {
    const isValid = moment(date).isValid();
    if (isValid) {
      const disabled = moment(date).isSame(moment(), 'day') ? true : moment(date).isAfter();
      setIsSaveDisabled(!disabled);
    } else {
      setIsSaveDisabled(true);
    }
  };

  const getDateFormat = (date) => {
    if (date) {
      return moment.utc(date).format(DATE_SHORT_FORMAT);
    }
    return '-';
  };

  const getDatePickerValueFormat = (date) => moment.utc(date)
    .utcOffset(0, false)
    .format(DATE_PICKER_VALUE_FORMAT);

  const setSubscriptionDate = () => {
    if (isDateEditMode) {
      return (
        <>
          <Grid item md={4} className={classes.subscriptionDate}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                fullWidth
                id="expiration-date"
                inputVariant="filled"
                size="small"
                format={DATE_PICKER_FORMAT}
                error={isSaveDisabled}
                helperText={isSaveDisabled ? t`Select a future date` : null}
                value={getDatePickerValueFormat(product.expirationDate)}
                minDate={new Date()}
                onChange={(date) => {
                  isValidDate(date);
                  setExpireDate(date);
                }}
                InputProps={{ classes: { root: classes.datePicker }, disableUnderline: true }}
                data-testid="expire-date-id"
              />
            </MuiPickersUtilsProvider>

          </Grid>
        </>
      );
    }
    return getDateFormat(product.expirationDate || '-');
  };

  const getInfoData = () => {
    if (product.productType === 'resource') {
      return {
        [t`Order Date: `]: getDateFormat(product.orderDate) || '-',
        [t`Order #: `]: product.orderNumber || '-',
        [t`Resource ID: `]: product.productId || '-',
        [t`Country Regulation: `]: product.regulation || '-',
        [t`Language: `]: product.language || '-',
        [t`Partner: `]: product.partner || '-',
        [t`Content Category:`]: <ContentCategoryViewer
          categories={product?.contentCategory}
        />,
      };
    } if (isComingFromSeatsDetailsPage) {
      return {
        [t`Order Date: `]: getDateFormat(product.orderDate) || '-',
        [t`Order #: `]: product.orderNumber || '-',
        [t`Course ID: `]: product.productId || '-',
        [t`Expiration Date: `]: setSubscriptionDate(),
        [t`Certificate Template: `]: product.certificateTemplateName || '-',
      };
    } return {
      [t`Order Date: `]: getDateFormat(product.orderDate) || '-',
      [t`Order #: `]: product.orderNumber || '-',
      [t`Course ID: `]: product.productId || '-',
      [t`Date Started: `]: getDateFormat(product.dateStarted) || '-',
      [t`Language: `]: product.language || '-',
      [t`Version: `]: product.version || '-',
      [t`Template: `]: product.certificateTemplateName || '-',
      [t`Expiration Date: `]: setSubscriptionDate(),
      [t`Certificate Issued: `]: getDateFormat(product.certificateIssueDate) || '-',
    };
  };
  const renderItem = (key, value) => (
    <Grid item md={6} xs={12} key={key}>
      <Typography
        className={classes.dataItemLabel}
        display="inline"
        variant="subtitle1"
      >
        {key}
      </Typography>
      { (key === 'Expiration Date:' && isDateEditMode) ? (
        <>
          &nbsp;{ value }
        </>
      ) : (
        <Typography display="inline" variant="subtitle1">
          &nbsp;{ value}
        </Typography>
      )}
    </Grid>
  );

  return (
    <>
      <Grid
        container
        justify="space-between"
        alignItems="center"
        className={classes.infoLabel}
        classes={{
          root: classes.headerRoot,
        }}
      >
        <Grid item classes={{ root: classes.dataItemLabel }}>
          <Typography variant="body2">
            <Trans>Information</Trans>
          </Typography>
        </Grid>
      </Grid>
      <Grid container alignItems="center" spacing={1} className={classes.infoArea}>
        {Object.entries(getInfoData()).map(([key, value]) => renderItem(key, value))}
      </Grid>
    </>
  );
}

UserProductInformation.defaultProps = {
  isComingFromSeatsDetailsPage: false,
};

UserProductInformation.propTypes = {
  product: PropTypes.shape({
    orderDate: PropTypes.string.isRequired,
    orderNumber: PropTypes.string.isRequired,
    productId: PropTypes.string.isRequired,
    certificateTemplateName: PropTypes.string,
    expirationDate: PropTypes.string.isRequired,
    language: PropTypes.string,
    dateStarted: PropTypes.string,
    version: PropTypes.string,
    certificateIssueDate: PropTypes.string,
    productType: PropTypes.string,
    regulation: PropTypes.string,
    partner: PropTypes.string,
    contentCategory: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        description: PropTypes.string,
      }),
    ),
  }).isRequired,
  isDateEditMode: PropTypes.bool.isRequired,
  isSaveDisabled: PropTypes.bool.isRequired,
  setExpireDate: PropTypes.func.isRequired,
  setIsSaveDisabled: PropTypes.func.isRequired,
  isComingFromSeatsDetailsPage: PropTypes.bool,
};

export default UserProductInformation;
